/**
 * Web Serial API Service — Browser-side UART communication with EV controllers.
 * Per-brand protocol support: checksums, framing, baud rates.
 * Requires HTTPS and Chrome/Edge 89+.
 */

// Per-brand checksum functions
const CHECKSUM_FNS = {
  fardriver: (bytes) => {
    // FarDriver: sum-mod-256
    let sum = 0;
    bytes.forEach(b => { sum = (sum + b) & 0xFF; });
    return sum;
  },
  sabvoton: (bytes) => {
    // Sabvoton: XOR of all bytes
    let xor = 0;
    bytes.forEach(b => { xor ^= b; });
    return xor;
  },
  kelly: (_bytes) => {
    // Kelly: no checksum
    return null;
  },
  ko_moto: (bytes) => {
    // KO Moto: XOR
    let xor = 0;
    bytes.forEach(b => { xor ^= b; });
    return xor;
  },
  ebmx: (bytes) => {
    // EBMX: sum-mod-256
    let sum = 0;
    bytes.forEach(b => { sum = (sum + b) & 0xFF; });
    return sum;
  },
  voltol: (bytes) => {
    // Voltol: CRC-8 (simplified as XOR for now)
    let xor = 0;
    bytes.forEach(b => { xor ^= b; });
    return xor;
  },
  votol: (bytes) => {
    // Votol: sum-mod-256
    let sum = 0;
    bytes.forEach(b => { sum = (sum + b) & 0xFF; });
    return sum;
  },
};

// Per-brand response frame parsing
const FRAME_PARSERS = {
  fardriver: (response) => {
    // FarDriver: [0x3A, len, ...data, checksum]
    if (response.length < 3 || response[0] !== 0x3A) return null;
    const len = response[1];
    if (response.length < len + 3) return null;
    return response.slice(2, 2 + len);
  },
  sabvoton: (response) => {
    // Sabvoton: [0xAA, 0x55, len, ...data]
    if (response.length < 4 || response[0] !== 0xAA || response[1] !== 0x55) return null;
    const len = response[2];
    return response.slice(3, 3 + len);
  },
  kelly: (response) => {
    // Kelly: [0x4B, 0x4C, ...data, 0x0D]
    if (response.length < 3 || response[0] !== 0x4B) return null;
    const end = response.indexOf(0x0D, 2);
    return end > 2 ? response.slice(2, end) : response.slice(2);
  },
  default: (response) => response, // passthrough
};

class SerialService {
  constructor() {
    this.port = null;
    this.reader = null;
    this.isConnected = false;
    this.onData = null;
    this.onDisconnect = null;
    this._readBuffer = [];
    this._reading = false;
    this.brand = null;
  }

  static isSupported() {
    return 'serial' in navigator;
  }

  getChecksumFn() {
    return CHECKSUM_FNS[this.brand] || CHECKSUM_FNS.sabvoton;
  }

  getFrameParser() {
    return FRAME_PARSERS[this.brand] || FRAME_PARSERS.default;
  }

  async connect(serialConfig, brandId) {
    if (!SerialService.isSupported()) {
      throw new Error('Web Serial API not supported. Use Chrome or Edge 89+.');
    }

    this.brand = brandId;

    try {
      this.port = await navigator.serial.requestPort();

      await this.port.open({
        baudRate: serialConfig.baudRate || 9600,
        dataBits: serialConfig.dataBits || 8,
        stopBits: serialConfig.stopBits || 1,
        parity: serialConfig.parity || 'none',
        bufferSize: 4096,
        flowControl: 'none',
      });

      this.isConnected = true;

      this.port.addEventListener('disconnect', () => {
        this.isConnected = false;
        if (this.onDisconnect) this.onDisconnect();
      });

      this._startReading();

      return { success: true, info: this.port.getInfo() };
    } catch (err) {
      this.isConnected = false;
      if (err.name === 'NotFoundError') {
        throw new Error('No serial port selected. Please try again and pick your controller.');
      }
      throw err;
    }
  }

  async _startReading() {
    if (!this.port?.readable || this._reading) return;
    this._reading = true;

    while (this.port?.readable && this._reading) {
      try {
        this.reader = this.port.readable.getReader();
        while (true) {
          const { value, done } = await this.reader.read();
          if (done) break;
          if (value) {
            const bytes = Array.from(value);
            this._readBuffer.push(...bytes);
            if (this.onData) this.onData(bytes);
          }
        }
      } catch (err) {
        if (err.name !== 'NetworkError') {
          console.error('Serial read error:', err);
        }
      } finally {
        if (this.reader) {
          try { this.reader.releaseLock(); } catch (e) { console.warn('Reader release:', e.message); }
          this.reader = null;
        }
      }
    }
    this._reading = false;
  }

  async sendBytes(bytes) {
    if (!this.port?.writable) throw new Error('Serial port not writable');
    const writer = this.port.writable.getWriter();
    try {
      await writer.write(new Uint8Array(bytes));
    } finally {
      writer.releaseLock();
    }
  }

  async sendAndReceive(bytes, timeoutMs = 2000) {
    this._readBuffer = [];
    await this.sendBytes(bytes);

    return new Promise((resolve) => {
      const startTime = Date.now();
      const check = () => {
        if (this._readBuffer.length > 0) {
          setTimeout(() => {
            const response = [...this._readBuffer];
            this._readBuffer = [];
            resolve(response);
          }, 100);
        } else if (Date.now() - startTime > timeoutMs) {
          resolve([]);
        } else {
          setTimeout(check, 50);
        }
      };
      check();
    });
  }

  async readParams(readCommand, paramAddresses) {
    const response = await this.sendAndReceive(readCommand, 3000);

    if (response.length === 0) {
      return { success: false, raw: [], error: 'No response from controller (timeout)' };
    }

    // Use brand-specific frame parser
    const parser = this.getFrameParser();
    const data = parser(response) || response;

    const params = {};
    const addrMap = {};
    Object.entries(paramAddresses).forEach(([name, addr]) => {
      addrMap[addr] = name;
    });

    for (let i = 0; i < data.length - 2; i++) {
      const addr = data[i];
      if (addrMap[addr]) {
        params[addrMap[addr]] = (data[i + 1] << 8) | data[i + 2];
      }
    }

    return { success: true, params, raw: response, bytesReceived: response.length };
  }

  async writeParam(writePrefix, address, value) {
    const valueBytes = [(value >> 8) & 0xFF, value & 0xFF];
    const cmd = [...writePrefix, address, ...valueBytes];

    // Use brand-specific checksum
    const checksumFn = this.getChecksumFn();
    const checksum = checksumFn(cmd);
    if (checksum !== null) cmd.push(checksum);

    const response = await this.sendAndReceive(cmd, 2000);

    return {
      success: response.length > 0,
      sentBytes: cmd,
      response,
      ackReceived: response.length > 0,
    };
  }

  /**
   * Flash firmware to controller via serial.
   * @param {Uint8Array} firmware - Binary firmware data
   * @param {Function} onProgress - Callback (percent, message)
   * @param {number} chunkSize - Bytes per chunk (default 128)
   */
  async flashFirmware(firmware, onProgress, chunkSize = 128) {
    if (!this.port?.writable) throw new Error('Serial port not writable');

    const totalChunks = Math.ceil(firmware.length / chunkSize);
    onProgress(0, 'Entering bootloader mode...');

    // Send bootloader entry command (brand-specific)
    const bootCmd = this.brand === 'fardriver' ? [0x3A, 0xFF, 0x01] :
                    this.brand === 'sabvoton' ? [0xAA, 0x55, 0xFF] :
                    this.brand === 'kelly' ? [0x4B, 0x4C, 0x46] :
                    [0xAA, 0xFF, 0x01];

    const bootResp = await this.sendAndReceive(bootCmd, 5000);
    if (bootResp.length === 0) {
      throw new Error('Controller did not enter bootloader mode. Check wiring.');
    }
    onProgress(2, 'Bootloader ready. Starting flash...');

    // Erase command
    const eraseCmd = [...bootCmd.slice(0, -1), 0x02];
    await this.sendAndReceive(eraseCmd, 10000);
    onProgress(5, 'Flash memory erased.');

    // Send firmware in chunks
    for (let i = 0; i < totalChunks; i++) {
      const offset = i * chunkSize;
      const chunk = firmware.slice(offset, offset + chunkSize);
      const addr = [(offset >> 24) & 0xFF, (offset >> 16) & 0xFF, (offset >> 8) & 0xFF, offset & 0xFF];

      const packet = [...bootCmd.slice(0, 2), 0x03, ...addr, chunk.length, ...Array.from(chunk)];
      const checksumFn = this.getChecksumFn();
      const cs = checksumFn(packet);
      if (cs !== null) packet.push(cs);

      await this.sendBytes(packet);

      // Wait for ACK every chunk
      const ack = await this.sendAndReceive([], 2000);
      if (ack.length === 0 || ack[0] === 0x15) { // NAK
        throw new Error(`Flash failed at offset 0x${offset.toString(16)}. No ACK.`);
      }

      const pct = 5 + Math.round((i / totalChunks) * 90);
      onProgress(pct, `Flashing... ${i + 1}/${totalChunks} chunks (${Math.round(offset / 1024)}KB)`);
    }

    // Verify command
    onProgress(96, 'Verifying firmware...');
    const verifyCmd = [...bootCmd.slice(0, 2), 0x04];
    const checksumFn = this.getChecksumFn();
    const fwChecksum = checksumFn(Array.from(firmware));
    if (fwChecksum !== null) verifyCmd.push(fwChecksum);

    const verifyResp = await this.sendAndReceive(verifyCmd, 5000);
    if (verifyResp.length === 0) {
      throw new Error('Firmware verification timeout.');
    }

    // Reset command
    onProgress(98, 'Resetting controller...');
    const resetCmd = [...bootCmd.slice(0, 2), 0x05];
    await this.sendBytes(resetCmd);

    onProgress(100, 'Firmware flashed successfully!');
    return { success: true, size: firmware.length, chunks: totalChunks };
  }

  async disconnect() {
    this._reading = false;

    if (this.reader) {
      try {
        await this.reader.cancel();
        this.reader.releaseLock();
      } catch (e) { console.warn('Reader cancel:', e.message); }
      this.reader = null;
    }

    if (this.port) {
      try { await this.port.close(); } catch (e) { console.warn('Port close:', e.message); }
      this.port = null;
    }

    this.isConnected = false;
    this._readBuffer = [];
  }

  getReadBuffer() { return [...this._readBuffer]; }
  clearBuffer() { this._readBuffer = []; }
}

export default SerialService;
